import uuid from 'node-uuid';
import Projection from 'ol/proj/projection'
import '../../../../Events.js';
import {NotImplementedError, RuntimeError} from '../../../../errors'

/** @namespace GIS.Map.Layer.OL3 */
NS("GIS.Map.Layer.OL3");

/**
 * @class
 * @extends Events
 */
GIS.Map.Layer.OL3.Base = Class(Events, /** @lends GIS.Map.Layer.OL3.Base.prototype */{
    // layer options
    /** @type {string} unique layer name */
    name: null,
    /** @type {string} Layer human name */
    title: null,
    opacity: 1,
    extent: null,
    minZoom: null,
    maxZoom: null,
    minResolution: null,
    maxResolution: null,
    projection: null,
    attributions: [],
    visibility: true,
    filter_: null,
    /** @type {boolean} Show layer as overlay */
    overlay: false,
    /**
     * @type {GIS.Map.OL3}
     * @protected
     */
    map: null,
    /**
     * @type {GIS.Map.Layer.Base}
     * @protected
     */
    base: null,
    /**
     * @type {ol.layer.Base}
     * @protected
     */
    layer: null,
    /**
     * @type {ol.source.Source}
     * @protected
     */
    source: null,
    /**
     * @type {Object.<string, ol.Overlay>}
     * @protected
     */
    popups: {},
    /**
     * @param {GIS.Map.OL3} map
     * @param {GIS.Map.Layer.Base} base
     * @param {Object} options
     */
    initialize(map, base, options)
    {
        this.map = map;
        this.base = base;

        options = this.prepareOptions(
            _.merge(
                {
                    name: uuid.v4()
                },
                Util.pickOptions(
                    this,
                    this.optionsNames()
                ),
                Util.pickOptions(
                    options,
//                    this.optionsNames()
                    _.keys(options)
                )
            )
        );
        _.merge(this, options);

        this.layer = this.createLayer(options);

        // set references to Glonass layers
        this.layer.setProperties({
            glLayer: this.base,
            glAdapterLayer: this
        });

        if (!options.overlay) {
            this.map.addVendorLayer(this.name, this.layer);
        }
    },
    /** @inheritDoc */
    optionsNames()
    {
        return ["maxZoom", "minZoom", "opacity", "extent", "projection", "attributions", "minResolution",
                "maxResolution", "overlay", "visibility", "filter_"];
    },
    /**
     * @param {Object} [options]
     * @protected
     */
    prepareOptions(options = {})
    {
        let layerOptions = _.merge({}, options);

        if (options.minZoom != null) {
            layerOptions.maxResolution = this.map.constrainResolution(this.map.getResolution(), options.minZoom - this.map.getZoom() - 1);
        }

        if (options.maxZoom != null) {
            layerOptions.minResolution = this.map.constrainResolution(this.map.getResolution(), options.maxZoom - this.map.getZoom());
        }

        return layerOptions;
    },
    /**
     * @param {Object} [options]
     * @returns {GIS.Map.Layer.OL3.Base}
     */
    updateOptions(options = {})
    {
        const keys = _.keys(options);
        options = Util.pickOptions(this.prepareOptions(options), keys);
//        options = this.prepareOptions(Util.pickOptions(options, this.optionsNames()));

        // set options that have setters
        const processed = [];
        _.forEach(options, (value, name) => {
            const method = 'set' + _.upperFirst(name);

            if (_.isFunction(this[method])) {
                this[method].call(this, value);
                processed.push(name);
            }
        });

        // merge other options
        _.assign(this, _.omit(options, processed));
        this.refresh();

        return this;
    },
    /**
     * @param {ol.proj.ProjectionLike} projection
     * @returns {GIS.Map.Layer.OL3.Base}
     */
    setProjection(projection)
    {
        this.setVendorProjection(projection);
        this.projection = projection instanceof Projection ? projection.getCode() : projection;

        return this;
    },
    /**
     * @param {number} zoom
     * @returns {GIS.Map.Layer.OL3.Base}
     */
    setMaxZoom(zoom)
    {
        this.maxZoom = zoom;
        this.minResolution = zoom != null ?
                             this.map.constrainResolution(this.map.getResolution(), zoom - this.map.getZoom()) :
                             null;

        this.layer.setMinResolution(this.minResolution != null ? this.minResolution : undefined);

        return this;
    },
    /**
     * @param {number} zoom
     * @returns {GIS.Map.Layer.OL3.Base}
     */
    setMinZoom(zoom)
    {
        this.minZoom = zoom;
        this.maxResolution = zoom != null ?
                             this.map.constrainResolution(this.map.getResolution(), zoom - this.map.getZoom() - 1) :
                             null;

        this.layer.setMaxResolution(this.maxResolution != null ? this.maxResolution : undefined);

        return this;
    },
    /**
     * @param {ol.proj.ProjectionLike} projection
     * @protected
     * @abstract
     */
    setVendorProjection(projection)
    {
        throw new NotImplementedError("abstract method `createLayer` isn't implemented");
    },
    /**
     * @returns {ol.layer.Base}
     */
    getVendor()
    {
        return this.layer;
    },
    /**
     * @returns {ol.source.Base}
     */
    getVendorSource()
    {
        return this.source;
    },
    /**
     * @param {Object} options
     * @returns {ol.layer.Layer}
     * @protected
     * @abstract
     */
    createLayer(options)
    {
        throw new NotImplementedError("abstract method `createLayer` isn't implemented");
    },
    /**
     * @fires GIS.Map.Layer.OL3.Base#destroy
     * @returns {GIS.Map.Layer.OL3.Base}
     */
    destroy()
    {
        this.emit("before.destroy");

        _.each(this.popups, this.closePopup, this);

        if (this.map) {
            this.map.off("click", this.onMapClick, this)
                .off("popup.close", this.onMapPopupClose, this);

            this.map.removeVendorLayer(this.name);
        }

        if (this.layer) {
            this.layer.unset("name");
            this.layer.unset("glLayer");
            this.layer.unset("glAdapterLayer");
        }

        this.attributions = [];
        this.projection = null;
        this.base = null;
        this.layer = null;
        this.map = null;
        this.source = null;
        /**
         * @event GIS.Map.Layer.OL3.Base#destroy
         */
        this.emit("destroy").off();

        return this;
    },
    /**
     * @returns {boolean}
     */
    visible()
    {
        return this.layer.getVisible();
    },
    /**
     * @fires GIS.Map.Layer.OL3.Base#show
     * @returns {GIS.Map.Layer.OL3.Base}
     */
    show()
    {
        this.layer.setVisible(true);

        this.map.on("click", this.onMapClick, this, true)
            .on("popup.close", this.onMapPopupClose, this);
        /**
         * @event GIS.Map.Layer.OL3.Base#show
         */
        this.emit("show");

        return this;
    },
    /**
     * @fires GIS.Map.Layer.OL3.Base#hide
     * @returns {GIS.Map.Layer.OL3.Base}
     */
    hide()
    {
        this.layer.setVisible(false);

        this.map.off("click", this.onMapClick, this)
            .off("popup.close", this.onMapPopupClose, this);
        /**
         * @event GIS.Map.Layer.OL3.Base#hide
         */
        this.emit("hide");

        return this;
    },
    /**
     * @fires GIS.Map.Layer.OL3.Base#opacity.change
     * @param {number} opacity Number in [0, 1] or [0, 100] range
     * @returns {GIS.Map.Layer.OL3.Base}
     */
    setOpacity(opacity)
    {
        this.layer.setOpacity(opacity > 1 ? opacity / 100 : opacity);
        /**
         * @event GIS.Map.Layer.OL3.Base#opacity.change
         */
        this.emit("opacity.change");

        return this;
    },
    /**
     * @returns {number}
     */
    getOpacity()
    {
        return this.layer.getOpacity();
    },
    /**
     * @fires GIS.Map.Layer.OL3.Base#refresh
     * @returns {GIS.Map.Layer.OL3.Base}
     */
    refresh()
    {
        this.layer.changed();
        this.source.refresh();
        /**
         * @event GIS.Map.Layer.OL3.Base#refresh
         */
        this.emit("refresh");

        return this;
    },
    /**
     * @param {number[]} pixel
     * @returns {boolean}
     */
    isHitLayer(pixel)
    {
        return this.map.forEachLayerAtPixel(pixel, layer => layer === this.layer);
    },
    /**
     * @returns {GIS.Map.Layer.Base}
     */
    getBaseLayer()
    {
        return this.base;
    },
    /**
     * @returns {GIS.Map.OL3}
     */
    getMap()
    {
        return this.map;
    },
    /**
     * Adds Popup overlay on map.
     *
     * @param {string|Element} content
     * @param {number[]} coordinate
     * @param {Object} [options] Additional data for popup
     * @returns {ol.Overlay|undefined}
     */
    openPopup(content, coordinate, options)
    {
        const name = _.get(options, "name") || uuid.v4();

        this.popups[name] = this.map.openPopup(content, coordinate, { ...options, name });

        return this.popups[name];
    },
    /**
     * Closes popup by name and removes it if needed
     *
     * @param {ol.Overlay|string} popup Popup object
     * @returns {ol.Overlay|undefined}
     */
    closePopup(popup)
    {
        return this.map && this.map.closePopup(popup);
    },
    /**
     * @param {{pixel: number[], coordinate: number[]}} e
     * @fires GIS.Map.Layer.OL3.Base#click Fires if event occurred somewhere on layer.
     * @protected
     */
    onMapClick(e)
    {
        if (this.isHitLayer(e.pixel)) {
            /**
             * @event GIS.Map.Layer.Base#click
             * @type {{pixel:Array.<number>, coordinate:Array.<number>}}
             */
            this.emit("click", e);
        }
    },
    /**
     * @param {{popup: ol.Overlay, coordinate: number[]}} e
     * @protected
     */
    onMapPopupClose(e)
    {
        this.popups = _.omitBy(this.popups, popup => popup === e.popup);
    },
    /**
     * Base method to load feature data. Should be overridden in child classes
     *
     * @param {number[]} coordinate
     * @param {Object} [options] Additional options
     * @returns {Promise.<Object>}
     */
    loadFeatureData(coordinate, options)
    {
        return Promise.reject(new RuntimeError("Nothing to load"));
    },
    /**
     * @param {Object|Function} filter
     */
    filter(filter) {
        this.filter_ = filter
    }
});

GIS.Map.Layer.OL3.Base.Static({
    /**
     * Applies url replaces.
     * @param {string} url
     * @param {Object} replaces
     * @param {string[]} [tokens]
     * @returns {string}
     */
    prepareURL(url, replaces, tokens = [])
    {
        const regExp = new RegExp(_.map(tokens, field => "(\\{" + field + "\\})").join("|"), "ig");

        return url.replace(regExp, match => Util.String.coalesce(replaces[match.substr(1, match.length - 2)]));
    },
    /**
     * @param {Array.<string>} attributions
     * @returns {Array.<ol.Attribution>}
     * @public
     */
    createAttributions(attributions)
    {
        return attributions
    }
});
